"use client";

import {
  RichTreeView,
  RichTreeViewProps,
  treeItemClasses,
  TreeViewBaseItem,
  TreeViewItemId,
  useTreeViewApiRef,
} from "@mui/x-tree-view";
import React, { useMemo } from "react";

import { MinusSquare, PlusSquare } from "@/icons";
import theme from "@/material";
import { alpha, Box, Skeleton } from "@mui/material";
import CustomTreeItem, { CustomTreeItemProps } from "./CustomTreeView";
export const buildTree = (
  items: ITreeViewItemModel[],
): TreeViewBaseItem<ITreeViewItemModel>[] => {
  const itemMap: { [key: string]: TreeViewBaseItem<ITreeViewItemModel> } = {};
  const rootItems: TreeViewBaseItem<ITreeViewItemModel>[] = [];

  items.forEach((item) => {
    itemMap[item.id] = { ...item, children: [] };
  });

  items.forEach((item) => {
    if (item.parentId) {
      const parent = itemMap[item.parentId];
      if (parent) {
        parent.children!.push(itemMap[item.id]);
      }
    } else {
      rootItems.push(itemMap[item.id]);
    }
  });

  return rootItems;
};
export const AppTreeView = ({
  slots,
  data,
  isLoading = false,
  onItemSelectionToggle,
  onSelectedItemsChange,
  checkboxSelection = true,
  selectedItems,
  selectTrigger = SelectTriggerEnum.CheckBox,
  expansionTrigger = "iconContainer",
  sx,
  rightIconAction,
  ...otherProps
}: IAppTreeViewProps) => {
  const toggledItemRef = React.useRef<{ [itemId: string]: boolean }>({});
  const apiRef = useTreeViewApiRef();
  const getItemDescendantsIds = (
    item: TreeViewBaseItem<ITreeViewItemModel>,
  ) => {
    const ids: string[] = [];
    item.children?.forEach((child) => {
      ids.push(child.id);
      ids.push(...getItemDescendantsIds(child));
    });
    return ids;
  };
  const getItemParentIds = (
    itemChild: TreeViewBaseItem<ITreeViewItemModel>,
  ) => {
    const itemIds: TreeViewItemId[] = [];
    const registerItemId = (item: TreeViewBaseItem<ITreeViewItemModel>) => {
      if (item.parentId !== null && item.parentId !== undefined) {
        itemIds.push(item.parentId);
        const parent = data.find((c) => c.id === item.parentId);
        if (parent) {
          registerItemId(parent);
        }
      }
    };
    registerItemId(itemChild);
    return itemIds;
  };
  const getItemParentHasChildSelectIds = (
    itemChild: TreeViewBaseItem<ITreeViewItemModel>,
    selectIds: string[],
  ) => {
    const itemIds: TreeViewItemId[] = [];
    const registerItemId = (
      item: TreeViewBaseItem<ITreeViewItemModel>,
      currentSelectIds: string[],
    ) => {
      if (item.parentId !== null && item.parentId !== undefined) {
        let nextSelectedIds = currentSelectIds;
        if (
          !data.some(
            (c) =>
              item.parentId === c.parentId &&
              currentSelectIds.findIndex((e) => e === c.id) > -1,
          )
        ) {
          itemIds.push(item.parentId);
          nextSelectedIds = nextSelectedIds.filter((c) => c !== item.parentId);
        }
        const parent = data.find((c) => c.id === item.parentId);
        if (parent) {
          registerItemId(parent, nextSelectedIds);
        }
      }
    };
    registerItemId(itemChild, selectIds);
    return itemIds;
  };
  const items = useMemo(() => {
    return buildTree(data);
  }, [data]);
  const handleItemSelectionToggle = (
    event: React.SyntheticEvent,
    itemId: string,
    isSelected: boolean,
  ) => {
    toggledItemRef.current[itemId] = isSelected;
    if (onItemSelectionToggle) onItemSelectionToggle(event, itemId, isSelected);
  };
  const handleSelectedItemsChange = (
    event: React.SyntheticEvent,
    newSelectedItems: string[],
  ) => {
    const getSelectedItemsRelationship = () => {
      const itemsToSelect: string[] = [];
      const itemsToUnSelect: { [itemId: string]: boolean } = {};
      Object.entries(toggledItemRef.current).forEach(([itemId, isSelected]) => {
        const item = apiRef.current!.getItem(itemId);
        if (isSelected) {
          itemsToSelect.push(...getItemDescendantsIds(item));
          itemsToSelect.push(...getItemParentIds(item));
        } else {
          getItemDescendantsIds(item).forEach((descendantId) => {
            itemsToUnSelect[descendantId] = true;
          });
          const allParents = getItemParentHasChildSelectIds(
            item,
            newSelectedItems,
          );
          if (allParents.length > 0) {
            allParents.forEach((parentId) => {
              itemsToUnSelect[parentId] = true;
            });
          }
        }
      });

      return Array.from(
        new Set(
          [...newSelectedItems, ...itemsToSelect].filter(
            (itemId) => !itemsToUnSelect[itemId],
          ),
        ),
      );
    };
    if (onSelectedItemsChange) {
      onSelectedItemsChange(
        event,
        newSelectedItems,
        getSelectedItemsRelationship,
      );
    }
    toggledItemRef.current = {};
  };
  const TreeItem = ({
    ...otherTreeItemProps
  }: Omit<
    CustomTreeItemProps,
    "selectItems" | "selectTrigger" | "checkboxSelection"
  >) => {
    return (
      <CustomTreeItem
        {...otherTreeItemProps}
        rightIconAction={rightIconAction}
        selectItems={selectedItems}
        selectTrigger={selectTrigger}
        checkboxSelection={checkboxSelection}
      />
    );
  };
  return (
    <>
      {!isLoading && (
        <RichTreeView
          slots={{
            item: TreeItem,
            expandIcon: PlusSquare,
            collapseIcon: MinusSquare,
            ...slots,
          }}
          items={items}
          selectedItems={selectedItems}
          sx={{
            border: "1px solid rgb(238 238 238)",
            background: "#fff",
            borderRadius: "8px",
            "& > li:not(:first-child)": {
              borderTop: "1px solid rgba(0, 0, 0, 0.12)",
            },
            ".MuiTreeItem2-root": {
              color: "common.black",
              position: "relative",
              [`& .${treeItemClasses.groupTransition}`]: {
                marginLeft: theme.spacing(3.5),
              },
              ".MuiCollapse-root": {
                paddingLeft: "32px",
              },
            },
            "& .content-root": {
              padding: "0px",
              flexDirection: "row-reverse",
              borderRadius: 0,
              marginBottom: 0,
              marginTop: 0,
              paddingRight: "8px",
              fontWeight: 500,
              alignItems: "flex-start",
              gap: 0,
              "& .content": {
                flex: "1 1 100%",
                display: "flex",
                alignItems: "flex-start",
                gap: "8px",
                padding: "8px 0",
                userSelect: "none",
              },
              "&.Mui-expanded:not(.Mui-focused, .Mui-selected, .Mui-selected.Mui-focused) .labelIcon":
                {
                  color: "primary.main",
                },
              "&.Mui-expanded::before": {
                content: '\"\"',
                display: "block",
                position: "absolute",
                left: "18px",
                top: "36px",
                height: "calc(100% - 48px)",
                width: "0.5px",
                borderLeft: "0.5px dashed",
                borderColor: "primary.main",
              },
              "&:hover, &.Mui-focused:hover, &.Mui-selected:hover": {
                backgroundColor: alpha(theme.palette.primary.main, 0.1),
                color: "primary.main",
              },
              "&.Mui-focused, &.Mui-selected, &.Mui-selected.Mui-focused": {
                backgroundColor: "transparent",
                color: "common.black",
              },
              "&.Mui-selected": {
                color: "primary.main",
              },
            },
            "& .icon-container": {
              fontSize: "14px",
              color: "primary.main",
              height: "36px",
              display: "flex",
              alignItems: "center",
              "& svg": {
                fontSize: "inherit",
              },
              padding: "8px 12px",
              width: "36px",
            },
            ...sx,
          }}
          checkboxSelection
          multiSelect
          apiRef={apiRef}
          onSelectedItemsChange={handleSelectedItemsChange}
          onItemSelectionToggle={handleItemSelectionToggle}
          expansionTrigger={
            selectTrigger === SelectTriggerEnum.LabelAndCheckBox
              ? "iconContainer"
              : expansionTrigger
          }
          {...otherProps}
        />
      )}
      {isLoading && (
        <Box className="p-2 border rounded-2">
          <Skeleton
            width="100%"
            height="40px"
            className="mb-1 transform-none"
          />
          <Skeleton
            width="100%"
            height="40px"
            className="mb-1 transform-none"
          />
          <Skeleton
            width="100%"
            height="40px"
            className="mb-1 transform-none"
          />
          <Skeleton
            width="100%"
            height="40px"
            className="mb-1 transform-none"
          />
          <Skeleton
            width="100%"
            height="40px"
            className="mb-1 transform-none"
          />
        </Box>
      )}
    </>
  );
};

export default AppTreeView;
export interface IAppTreeViewProps extends Omit<
  RichTreeViewProps<ITreeViewItemModel, true>,
  | "items"
  | "multiSelect"
  | "onSelectedItemsChange"
  | "apiRef"
  | "defaultSelectedItems"
  | "defaultChecked"
  | "defaultValue"
> {
  data: ITreeViewItemModel[];
  onSelectedItemsChange?: (
    event: React.SyntheticEvent,
    newSelectedItems: string[],
    getSelectedItemsRelationship: () => string[],
  ) => void;
  selectTrigger?: SelectTriggerEnum;
  isLoading?: boolean;
  rightIconAction?: (
    data: TreeViewBaseItem<ITreeViewItemModel>,
  ) => React.ReactNode;
}
export interface ITreeViewItemModel {
  id: string;
  label: string | React.ReactElement;
  parentId?: string;
  code?: string;
  description?: string | null;
  order?: number;
  isActive?: boolean;
  children?: ITreeViewItemModel[];
}
export enum SelectTriggerEnum {
  CheckBox,
  LabelAndCheckBox,
}
